/** "Train of thought" trail for tool turns; no wire flag separates intermediate from final text, so the final
 *  answer is the LAST text part — which always renders full-style OUTSIDE the trail (streaming and settled), so
 *  it never jumps on settle (APP-3413). The trail holds only intermediate steps: tool parts + non-last text. */

import { useState } from "react"
import { Box, Collapse, Typography, styled } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { CheckCircleOutlined, ChevronRight, ErrorOutlined } from "@mui/icons-material"
import { useMessageContext } from "@mui/x-chat-headless"
import type { ChatDynamicToolMessagePart, ChatMessage, ChatMessagePart, ChatTextMessagePart, ChatToolMessagePart } from "@mui/x-chat-headless"
import { chatCodeBlockClasses } from "@mui/x-chat"
import { ChatStatusSpinner, WorkingPulse } from "./chat-pill"
import { ChatMarkdown } from "./chat-markdown"
import { toolDisplayLabel } from "./tool-labels"

type ToolPart = ChatToolMessagePart | ChatDynamicToolMessagePart
const RUNNING_STATES = new Set(["input-streaming", "input-available", "approval-requested", "approval-responded"])

function isToolPart(part: ChatMessagePart): part is ToolPart {
  return part.type === "tool" || part.type === "dynamic-tool"
}

function isTextPart(part: ChatMessagePart): part is ChatTextMessagePart {
  return part.type === "text"
}

function isRunning(part: ToolPart): boolean {
  return part.toolInvocation ? RUNNING_STATES.has(part.toolInvocation.state) : false
}

function isError(part: ToolPart): boolean {
  const state = part.toolInvocation?.state
  return state === "output-error" || state === "output-denied"
}

/** True when the turn made (or is making) a tool call — gates the trail. */
export function hasToolParts(message: ChatMessage): boolean {
  return message.parts.some(isToolPart)
}

/** Intermediate steps in order: tool parts + every text part EXCEPT the last (the final answer always renders
 *  outside the trail, so it's never a step — streaming or settled). Exported for unit coverage of that invariant. */
export function getTrailSteps(message: ChatMessage): ChatMessagePart[] {
  const lastTextIndex = message.parts.map(isTextPart).lastIndexOf(true)
  return message.parts.filter((part, index) => isToolPart(part) || (isTextPart(part) && index !== lastTextIndex))
}

function toolLabel(part: ToolPart): string {
  return toolDisplayLabel(part.toolInvocation?.title, part.toolInvocation?.toolName)
}

function formatPayload(output: unknown): string {
  if (output == null) return ""
  if (typeof output === "string") return output
  if (typeof output === "object" && "result" in output && typeof (output as { result: unknown }).result === "string") {
    const { result } = output as { result: string }
    try {
      return JSON.stringify(JSON.parse(result), null, 2)
    } catch {
      return result
    }
  }
  try {
    return JSON.stringify(output, null, 2)
  } catch {
    return String(output)
  }
}

const ROOT_SX = { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5, mb: 1 } satisfies SxProps<Theme>
const TIMELINE_SX = { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5, pl: 0.25, width: "100%" } satisfies SxProps<Theme>
const ROW_SX = { display: "flex", alignItems: "center", gap: 0.75 } satisfies SxProps<Theme>
// Clamp markdown to the trail's muted 0.875rem type so headings/lists don't explode inside the trail.
const STEP_SX = {
  fontSize: "0.875rem", color: "var(--app-text-secondary)", pl: "1.4rem",
  // -child, not -of-type: ChatMarkdown's Fragment wrapper leaves heterogeneous children, and -of-type clamps the
  // first/last of every tag rather than the step's true edges.
  "& > :first-child": { mt: 0 }, "& > :last-child": { mb: 0 },
  "& p": { m: 0 },
  "& h1, & h2, & h3, & h4, & h5, & h6": { fontSize: "0.875rem", fontWeight: 600, m: 0 },
  // Tripled so this clamp out-ranks a class-on-the-element list rule on the markdown surface (0,3,1 beats 0,3,0),
  // which would otherwise replace the trail's compaction the first time a tool emitted a list. No such rule exists
  // here yet — APP-3476 (#4202) adds one, and may land either side of this.
  "&&& ul, &&& ol": { m: 0, pl: "1.25rem" },
  "& li + li": { mt: 0.25 },
  "& :not(pre) > code": { fontFamily: "monospace", backgroundColor: "transparent", fontSize: "inherit", p: 0 },
  // Exclude the code card's <pre> — it has its own inset + typography, and STEP_SX's 0.75rem/pre-wrap would flatten it.
  [`& pre:not(.${chatCodeBlockClasses.pre})`]: { m: 0, fontSize: "0.75rem", whiteSpace: "pre-wrap", wordBreak: "break-word" },
} satisfies SxProps<Theme>
const LABEL_SX = { fontSize: "0.875rem", color: "var(--app-text-secondary)" } satisfies SxProps<Theme>
const LABEL_ERROR_SX = { fontSize: "0.875rem", color: "var(--opcolor-red-dark)" } satisfies SxProps<Theme>
const BULLET_SX = { fontSize: "0.9rem", flexShrink: 0, color: "var(--app-text-secondary)" } satisfies SxProps<Theme>
const BULLET_ERROR_SX = { fontSize: "0.9rem", flexShrink: 0, color: "var(--opcolor-red-dark)" } satisfies SxProps<Theme>
const PAYLOAD_SX = {
  m: 0, mt: 0.5, ml: "1.4rem", pl: "0.75rem", borderLeft: "1px solid var(--opcolor-grey-lighter)",
  fontFamily: "monospace", fontSize: "0.75rem", color: "var(--app-text-secondary)",
  maxHeight: "16rem", overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word",
} satisfies SxProps<Theme>

const DisclosureButton = styled("button")({
  display: "inline-flex", alignItems: "center", gap: "0.5rem",
  border: 0, padding: 0, margin: 0, background: "none", cursor: "pointer",
  font: "inherit", fontSize: "0.875rem", color: "var(--app-text-secondary)", textAlign: "left",
})

const Chevron = styled(ChevronRight, { shouldForwardProp: (prop) => prop !== "open" })<{ open: boolean }>(
  ({ theme, open }) => ({
    fontSize: "1rem", flexShrink: 0,
    transition: theme.transitions.create("transform", { duration: theme.transitions.duration.shortest }),
    transform: open ? "rotate(90deg)" : "none",
  }),
)

function StatusBullet({ part }: { part: ToolPart }) {
  if (isError(part)) return <ErrorOutlined sx={BULLET_ERROR_SX} aria-hidden />
  if (isRunning(part)) return <ChatStatusSpinner aria-hidden />
  return <CheckCircleOutlined sx={BULLET_SX} aria-hidden />
}

function ToolStep({ part }: { part: ToolPart }) {
  const [isOpen, setIsOpen] = useState(false)
  const invocation = part.toolInvocation
  const label = toolLabel(part)
  if (!invocation) return null
  const { errorText, output } = invocation
  const bullet = <StatusBullet part={part} />

  if (isError(part)) {
    return <Box sx={ROW_SX}>{bullet}<Typography component="span" sx={LABEL_ERROR_SX}>Failed {label}{errorText ? `: ${errorText}` : ""}</Typography></Box>
  }
  if (isRunning(part)) {
    return <Box sx={ROW_SX}>{bullet}<Typography component="span" sx={LABEL_SX}>Running {label}…</Typography></Box>
  }
  const payload = formatPayload(output)
  if (!payload) return <Box sx={ROW_SX}>{bullet}<Typography component="span" sx={LABEL_SX}>Ran {label}</Typography></Box>
  return (
    <Box>
      <DisclosureButton type="button" aria-expanded={isOpen} onClick={() => setIsOpen((open) => !open)}>
        {bullet}<span>Ran {label}</span><Chevron open={isOpen} aria-hidden />
      </DisclosureButton>
      <Collapse in={isOpen} unmountOnExit>
        <Box component="pre" sx={PAYLOAD_SX}>{payload}</Box>
      </Collapse>
    </Box>
  )
}

function withToolCount(head: string, toolCount: number): string {
  return toolCount > 0 ? `${head} — ${toolCount} tool call${toolCount === 1 ? "" : "s"}` : head
}

function workingLabelFor(steps: ChatMessagePart[]): string {
  const runningTool = [...steps].reverse().find((part): part is ToolPart => isToolPart(part) && isRunning(part))
  return withToolCount(runningTool ? `Running ${toolLabel(runningTool)}` : "Thinking", steps.filter(isToolPart).length)
}

/** The live status text, shared with the bottom-anchored working cue so the two can never word it differently. */
export function getWorkingLabel(message: ChatMessage): string {
  return workingLabelFor(getTrailSteps(message))
}

export function ThinkingTrail({ message }: { message: ChatMessage }) {
  const { streaming } = useMessageContext()
  // Seeded open while streaming, then the user's toggle owns it; a functional updater so two fast clicks flip twice.
  const [isOpen, setIsOpen] = useState(streaming)
  const steps = getTrailSteps(message)
  if (steps.length === 0 && !streaming) return null

  const toolCount = steps.filter(isToolPart).length
  const status = streaming
    ? workingLabelFor(steps)
    : withToolCount(`Worked through ${steps.length} ${steps.length === 1 ? "step" : "steps"}`, toolCount)

  return (
    <Box sx={ROOT_SX} data-testid="chatbox-thinking-trail">
      <DisclosureButton
        type="button"
        aria-expanded={isOpen}
        data-testid="chatbox-thinking-toggle"
        onClick={() => setIsOpen((open) => !open)}
      >
        <Chevron open={isOpen} aria-hidden />
        <WorkingPulse pulsing={streaming}>{status}</WorkingPulse>
      </DisclosureButton>
      <Collapse in={isOpen} unmountOnExit>
        <Box sx={TIMELINE_SX}>
          {steps.map((part, index) =>
            isToolPart(part) ? (
              <ToolStep key={index} part={part} />
            ) : (
              <Box key={index} sx={STEP_SX}><ChatMarkdown>{isTextPart(part) ? part.text : ""}</ChatMarkdown></Box>
            ),
          )}
        </Box>
      </Collapse>
    </Box>
  )
}
